import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { node, nodeWithArgs, toQuery, toMutation, enumeration } from 'common/util/queryHelper';
import { addApplicationMessages } from 'common/actions/applicationMessagesActions';
import lodash from 'lodash';
import { decrementUnfiledDocuments } from '../../binder/actions/index';
import { removeDocumentFromListAction } from '../../pages/files/actions/files';
import { binderInstitutionIdSelector } from 'common/selectors';

export const PART11_WRITE_DOCUMENT = 'PART11_LOAD_DOCUMENT';
export const PART11_REMOVE_DOCUMENT = 'PART11_REMOVE_DOCUMENT';
export const LOAD_ME_BINDER_PERMISSIONS = 'LOAD_ME_BINDER_PERMISSIONS';

export function loadMeBinderPermissions(binderId) {
    return (dispatch, getState) => {
        const query = node('me', [
            nodeWithArgs('binderPermissions', { binderId }),
        ]);

        return queryGraphQL(toQuery(query)).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', 'Failed to load binder permissions for user'));
            }

            dispatch({
                type: LOAD_ME_BINDER_PERMISSIONS,
                payload: JSON.parse(response.data.me.binderPermissions),
            });
        });
    };
}

export function loadDocumentHistory({ documentId, binderId }, dateRange) {
    return (dispatch, getState) => {
        const query = nodeWithArgs('document', {
            id: documentId,
            binderId
        }, [
            'id',
            nodeWithArgs('history', {
                dateRange: {
                    start: dateRange.start.toISOString(),
                    end: dateRange.end.toISOString()
                }
            }, [
                'date',
                node('actions', [
                    'action',
                    'timestamp',
                    node('data', [
                        'type',
                        'value'
                    ]),
                    node('user', [
                        'sub',
                        'name',
                        'emailAddress'
                    ]),
                    'message',
                ])
            ])
        ]);

        return queryGraphQL(toQuery(query)).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            } else {
                const previousDocument = lodash.get(getState(), `documents.${documentId}`);
                const updatedDocument = { ...previousDocument, ...response.data.document };
                dispatch(writeDocument(updatedDocument));
            }
        });
    };
}

export function loadDocument(documentId, { dateRange, binderId, includeRenditions = true } = {}) {
    dateRange = dateRange ? {
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString()
    } : {};

    return dispatch => {
        const query = nodeWithArgs('document', {
            id: documentId,
            binderId
        }, [
            'id',
            'name',
            'documentStatus',
            'conversionStatus',
            node('uploadUser', [
                'sub',
                'displayName',
                'emailAddress'
            ]),
            node('commitUser', [
                'sub',
                'displayName',
                'emailAddress'
            ]),
            'commitDate',
            'uploadDate',
            'institutionTimezone',
            node('descriptors', [
                'id',
                'dataType',
                'displayName',
                'values',
                'parent'
            ]),
            node('documentType', [
                'id',
                'displayName'
            ]),
            includeRenditions ? nodeWithArgs('renditions', {
                type: enumeration('VIEW'),
            }, [
                'objectName',
                'containerId',
                'prettyFileName',
                'fileExt',
                'mimeType',
                'fileSize',
                'filePurpose',
                'createdDate',
                'type',
                node('downloadRequest', [
                    'tempUrl'
                ])
            ]) : null,
            nodeWithArgs('history', {
                dateRange
            }, [
                'date',
                node('actions', [
                    'action',
                    'timestamp',
                    node('data', [
                        'type',
                        'value'
                    ]),
                    node('user', [
                        'sub',
                        'name',
                        'emailAddress'
                    ]),
                    'message'
                ])
            ]),
            node('signatures', [
                node('user', ['sub', 'name', 'emailAddress']),
                node('statement', ['id', 'name', 'statement']),
                'dateSigned'
            ]),
        ]);

        return queryGraphQL(toQuery(query)).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            } else {
                dispatch(writeDocument(response.data.document));
            }
        });
    };
}

function writeDocument(document) {
    return {
        type: PART11_WRITE_DOCUMENT,
        payload: document
    };
}

export function deleteUnfiledDocument(documentId, shouldDeleteFileMetadata) {
    return (dispatch, getState) => {
        const state = getState();
        const binderId = lodash.get(state, 'binder.id');
        const institutionId = binderInstitutionIdSelector(state);

        const mutation = toMutation(nodeWithArgs('deleteUnfiledDocument', {
            id: documentId,
            binderId,
            institutionId,
            shouldDeleteFileMetadata
        }));

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            } else {
                dispatch(removeDocument(documentId));
                dispatch(decrementUnfiledDocuments());
                dispatch(removeDocumentFromListAction(documentId));
            }
        });
    };
}

export function removeDocument(documentId) {
    return {
        type: PART11_REMOVE_DOCUMENT,
        payload: { documentId }
    };
}

export function documentDownloadRequest(documentId, binderId, renditionType) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const query = nodeWithArgs('document', {
            id: documentId,
            binderId,
            downloadRenditionType: renditionType,
            institutionId
        }, [
            'id',
            'documentStatus',
            nodeWithArgs('renditions', {
                type: enumeration(renditionType),
            }, [
                node('downloadRequest', [
                    'tempUrl'
                ])
            ])
        ]);

        return queryGraphQL(toQuery(query)).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            }
            else {
                const downloadUrl = lodash.get(response, 'data.document.renditions[0].downloadRequest.tempUrl');
                if (downloadUrl) {
                    dispatch(addApplicationMessages('download', [downloadUrl], { ttl: 0 }));
                }
                else {
                    dispatch(addApplicationMessages('error', ['An error occurred with your download request. Please try again later.']));
                }
            }
        });
    };
}
